import { useRef, useCallback, useEffect } from 'react';
import { EventKey } from '@/common/constants';

interface EventCacheEntry {
  timestamp: number;
  timeoutId: ReturnType<typeof setTimeout>;
}

type TrackEventFunction = (
  eventKey: EventKey,
  extraParams?: Record<string, unknown>
) => Promise<void>;

// Stable JSON serialization that sorts keys alphabetically
const stableStringify = (obj: Record<string, unknown>): string => {
  return JSON.stringify(obj, Object.keys(obj).sort());
};

export const useEventDeduplication = (trackEvent: TrackEventFunction) => {
  const eventsCache = useRef(new Map<string, EventCacheEntry>());

  const trackEventWithDedup = useCallback(
    async (
      eventKey: EventKey,
      extraParams: Record<string, unknown> = {},
      windowMs: number = 5000
    ): Promise<void> => {
      const eventSignature = `${eventKey}-${stableStringify(extraParams)}`;
      const now = Date.now();
      const cachedEvent = eventsCache.current.get(eventSignature);

      if (!cachedEvent || now - cachedEvent.timestamp > windowMs) {
        // Clear existing timeout if any
        if (cachedEvent?.timeoutId) {
          clearTimeout(cachedEvent.timeoutId);
        }

        try {
          // Track the event
          await trackEvent(eventKey, extraParams);
        } catch (error) {
          console.error(`Error tracking event "${eventKey}" with params:`, extraParams, error);
          return;
        }

        // Set up automatic cleanup
        const timeoutId = setTimeout(() => {
          eventsCache.current.delete(eventSignature);
        }, windowMs);

        // Store in cache
        eventsCache.current.set(eventSignature, {
          timestamp: now,
          timeoutId,
        });
      } else {
        console.debug(`Event ${eventKey} blocked - duplicate within ${windowMs}ms`, {
          eventKey,
          extraParams,
          timeSinceLastEvent: now - cachedEvent.timestamp,
          windowMs,
        });
      }
    },
    [trackEvent]
  );

  // Cleanup function to clear all timeouts on unmount
  const cleanup = useCallback(() => {
    eventsCache.current.forEach((entry) => {
      clearTimeout(entry.timeoutId);
    });
    eventsCache.current.clear();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return { trackEventWithDedup, cleanup };
};
